import { AxiosInstance, AxiosResponse } from "axios";
import {
    ApiKey,
    ApiKeyCreated,
    ApiKeyListScope,
    ProductScope,
    TokenAccessScope
} from "../types";

/**
 * Customer API tokens live on chopper-backend, not on `customer-api`.
 *
 * That is deliberate, not an accident of where the code was easiest to put:
 * issuing a token is a dashboard-user action, and this is the dashboard's
 * backend, so the session and the tenant are already established. The public
 * API only validates what a customer presents to it.
 */
const BASE = "/customer-api/tokens";

interface ApiEnvelope<T> {
    success: boolean;
    data: T;
    message?: string;
}

interface ListEnvelope extends ApiEnvelope<ApiKey[]> {
    scope: ApiKeyListScope;
}

/**
 * Omitting `tenantId` is meaningful, not a shortcut: the backend then
 * answers with everything the caller may see — its own tenant plus the
 * tenants below it. That is the whole point of the "All tenants" lens.
 */
export function listApiKeys(
    api: AxiosInstance,
    tenantId?: number
): Promise<AxiosResponse<ListEnvelope>> {
    const qs = tenantId ? `?tenantId=${tenantId}` : "";
    return api.get(`${BASE}${qs}`);
}

export interface CreateApiKeyBody {
    name: string;
    /** At least one. The backend rejects an empty array. */
    productScopes: ProductScope[];
    /** Defaults to `own`. `subtree` is refused on a customer-level tenant. */
    accessScope?: TokenAccessScope;
    /** Days until it stops working. Omitted means the backend default (90). */
    expiresInDays?: number;
}

export function createApiKey(
    api: AxiosInstance,
    body: CreateApiKeyBody,
    tenantId?: number
): Promise<AxiosResponse<ApiEnvelope<ApiKeyCreated>>> {
    const qs = tenantId ? `?tenantId=${tenantId}` : "";
    return api.post(`${BASE}${qs}`, body);
}

export function revokeApiKey(
    api: AxiosInstance,
    tokenId: string,
    tenantId?: number
): Promise<AxiosResponse<ApiEnvelope<{ id: string; revokedAt: string }>>> {
    const qs = tenantId ? `?tenantId=${tenantId}` : "";
    return api.delete(`${BASE}/${tokenId}${qs}`);
}
